import React from 'react';
import { personalInfo } from '../data/mock';
import { useInView } from '../hooks/useInView';
import { Button } from './ui/button';
import { Coffee, Calendar, Linkedin, Code2, Trophy, Github, Heart } from 'lucide-react';

const ContactSection = () => {
  const [headerRef, headerInView] = useInView({ threshold: 0.3 });
  const [coffeeRef, coffeeInView] = useInView({ threshold: 0.2 });
  const [meetRef, meetInView] = useInView({ threshold: 0.2 });
  const calendlyUrl = personalInfo.calendlyUrl || 'https://calendly.com';

  const socialLinks = [
    { icon: Linkedin, href: personalInfo.social.linkedin, label: 'LinkedIn' },
    { icon: Code2, href: personalInfo.social.leetcode, label: 'LeetCode' },
    { icon: Trophy, href: personalInfo.social.codeforces, label: 'Codeforces' },
    { icon: Github, href: personalInfo.social.github, label: 'GitHub' },
  ];

  return (
    <>
      <section id="contact" className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div
            ref={headerRef}
            className={`mb-16 transition-all duration-700 ${
              headerInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <p className="font-mono text-[12px] text-gray-400 tracking-widest uppercase mb-4">
              Get in Touch
            </p>
            <h2 className="font-heading text-4xl md:text-[3.25rem] font-[200] tracking-tight leading-tight">
              Let's Connect<span className="text-gray-200">.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Buy Me a Coffee */}
            <div
              ref={coffeeRef}
              className={`transition-all duration-700 ${
                coffeeInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <div className="p-8 md:p-10 rounded-3xl bg-[#FAFAFA] border border-gray-100 h-full flex flex-col">
                <div className="w-14 h-14 rounded-2xl bg-white border border-gray-100 flex items-center justify-center mb-6 shadow-[0_1px_4px_rgba(0,0,0,0.03)]">
                  <Coffee size={22} className="text-gray-600" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-2xl font-semibold tracking-tight mb-3">
                  Buy Me a Coffee
                </h3>
                <p className="text-gray-400 leading-relaxed text-[15px] mb-8 flex-grow">
                  If you find my work helpful or enjoy my open-source contributions,
                  consider buying me a coffee. It keeps me fueled for late-night coding
                  sessions and weekend side-projects.
                </p>
                <Button
                  className="w-full h-12 rounded-full bg-[#0a0a0a] text-white hover:bg-[#262626] transition-all duration-300 text-[13px] font-medium"
                  onClick={() => window.open('https://buymeacoffee.com/josbeak', '_blank')}
                >
                  <Coffee size={15} className="mr-2" />
                  Support My Work
                </Button>
              </div>
            </div>

            {/* Book a Meeting — Calendly */}
            <div
              ref={meetRef}
              className={`transition-all duration-700 delay-100 ${
                meetInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <div className="p-8 md:p-10 rounded-3xl bg-[#FAFAFA] border border-gray-100 h-full flex flex-col">
                <div className="w-14 h-14 rounded-2xl bg-white border border-gray-100 flex items-center justify-center mb-6 shadow-[0_1px_4px_rgba(0,0,0,0.03)]">
                  <Calendar size={22} className="text-gray-600" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-2xl font-semibold tracking-tight mb-2">
                  Book a Meeting
                </h3>
                <p className="text-gray-400 leading-relaxed text-[15px] mb-8 flex-grow">
                  Schedule a call at a time that works for you. I&apos;ll send a calendar invite and we can chat.
                </p>
                <Button
                  className="w-full h-12 rounded-full bg-[#0a0a0a] text-white hover:bg-[#262626] transition-all duration-300 text-[13px] font-medium"
                  onClick={() => window.open(calendlyUrl, '_blank')}
                >
                  <Calendar size={15} className="mr-2" />
                  Open Calendly
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-12">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <span className="font-mono text-[12px] text-gray-300 tracking-wide">
              {new Date().getFullYear()} — {personalInfo.name}
            </span>

            <div className="flex items-center gap-6">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-black transition-colors duration-300"
                  title={social.label}
                >
                  <social.icon size={15} strokeWidth={1.5} />
                </a>
              ))}
            </div>

            <p className="text-[11px] text-gray-300 flex items-center gap-1.5 font-mono">
              Crafted with <Heart size={11} /> & caffeine
            </p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default ContactSection;
